import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BlogCard from "@/components/blog/BlogCard";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import DesktopFloatingCTA from "@/components/DesktopFloatingCTA";
import { FloatingIcons } from "@/components/ui/floating-icons";
import { useLanguage } from "@/contexts/LanguageContext";

const blogPosts = [
  {
    slug: "what-is-ai-ugc",
    title: "What is AI UGC? The Complete 2026 Guide",
    excerpt: "Learn everything about AI-generated user content and how it's revolutionizing marketing for businesses worldwide.",
    date: "January 5, 2026",
    readTime: "8 min read",
    category: "Guide",
    categoryColor: "from-neon-purple to-neon-pink",
    featured: true,
  },
  {
    slug: "buy-ugc-videos-guide",
    title: "How to Buy UGC Videos: Complete Buyer's Guide",
    excerpt: "Everything you need to know about buying UGC videos. Compare prices, platforms, and get the best ROI.",
    date: "January 8, 2026",
    readTime: "10 min read",
    category: "Guide",
    categoryColor: "from-neon-green to-neon-cyan",
    featured: true,
  },
  {
    slug: "ai-ugc-vs-traditional-ugc",
    title: "AI UGC vs Traditional UGC: Which is Better?",
    excerpt: "Compare cost, speed, and quality between AI-generated content and traditional influencer UGC.",
    date: "January 4, 2026",
    readTime: "6 min read",
    category: "Comparison",
    categoryColor: "from-neon-cyan to-neon-purple",
  },
  {
    slug: "ugc-for-meta-ads",
    title: "UGC for Meta Ads: Facebook & Instagram Strategy",
    excerpt: "Master UGC for Meta Ads. Create high-converting Facebook and Instagram ads using user-generated content.",
    date: "January 8, 2026",
    readTime: "9 min read",
    category: "Strategy",
    categoryColor: "from-blue-500 to-neon-purple",
  },
  {
    slug: "ai-ugc-vs-influencer-marketing",
    title: "AI UGC vs Influencer Marketing: Which Wins in 2026?",
    excerpt: "Compare AI UGC to traditional influencer marketing. Cost, speed, ROI, and scalability breakdown.",
    date: "January 8, 2026",
    readTime: "8 min read",
    category: "Comparison",
    categoryColor: "from-neon-cyan to-neon-purple",
  },
  {
    slug: "best-ai-ugc-generator-tools",
    title: "Best AI UGC Generator Tools 2026: Complete Comparison",
    excerpt: "Compare top AI UGC generator tools. Features, pricing, pros and cons of Fuel72, Synthesia, HeyGen, and more.",
    date: "January 8, 2026",
    readTime: "12 min read",
    category: "Tools",
    categoryColor: "from-neon-green to-neon-cyan",
  },
  {
    slug: "what-is-fuel72",
    title: "What is Fuel72? Your AI UGC Partner",
    excerpt: "Discover how Fuel72 helps businesses boost their visibility with professional AI UGC videos in 72 hours.",
    date: "January 3, 2026",
    readTime: "5 min read",
    category: "About",
    categoryColor: "from-orange-500 to-neon-pink",
  },
  {
    slug: "why-online-visibility-matters-2026",
    title: "Why Being Visible Online in 2026 is a Must",
    excerpt: "No content means no business. Learn why online visibility is non-negotiable in 2026.",
    date: "January 2, 2026",
    readTime: "7 min read",
    category: "Strategy",
    categoryColor: "from-neon-green to-neon-cyan",
  },
  {
    slug: "case-studies-ugc-results",
    title: "Real Results: 40%+ Sales Increase with AI UGC",
    excerpt: "See how businesses achieved 1600% more impressions and 35% revenue uplift with Fuel72.",
    date: "January 1, 2026",
    readTime: "10 min read",
    category: "Case Studies",
    categoryColor: "from-neon-pink to-orange-500",
  },
  {
    slug: "how-to-boost-business-with-ai-ugc",
    title: "How to Boost Your Business with AI UGC",
    excerpt: "A step-by-step guide to leveraging AI UGC for maximum business growth.",
    date: "December 30, 2025",
    readTime: "9 min read",
    category: "Tutorial",
    categoryColor: "from-neon-purple to-neon-cyan",
  },
];

const germanPosts = [
  {
    slug: "ugc-agentur-vs-freelancer",
    title: "UGC Agentur vs. Freelancer: Was ist besser?",
    excerpt: "Vergleich zwischen UGC Agentur und Freelancer Creator. Kosten, Qualität, Zuverlässigkeit.",
    date: "8. Januar 2026",
    readTime: "10 Min. Lesezeit",
    category: "Vergleich",
    categoryColor: "from-neon-purple to-neon-pink",
    featured: true,
  },
  {
    slug: "ugc-kosten-preise-2026",
    title: "UGC Kosten & Preise 2026: Was kostet UGC?",
    excerpt: "Kompletter UGC Preisguide. Was kosten UGC Videos bei Agenturen, Freelancern und AI?",
    date: "8. Januar 2026",
    readTime: "8 Min. Lesezeit",
    category: "Guide",
    categoryColor: "from-neon-cyan to-neon-purple",
  },
  {
    slug: "ki-avatar-videos-marketing",
    title: "KI Avatar Videos für Marketing: Der komplette Guide",
    excerpt: "Alles über KI Avatar Videos für Marketing. Wie funktionieren AI Avatare? Kosten und Anbieter.",
    date: "8. Januar 2026",
    readTime: "9 Min. Lesezeit",
    category: "Technologie",
    categoryColor: "from-neon-green to-neon-cyan",
  },
  {
    slug: "tiktok-reels-ads-erstellen",
    title: "TikTok & Reels Ads erstellen: Der komplette Guide",
    excerpt: "So erstellst du erfolgreiche TikTok und Instagram Reels Ads. Best Practices und AI-Tools.",
    date: "8. Januar 2026",
    readTime: "11 Min. Lesezeit",
    category: "Tutorial",
    categoryColor: "from-orange-500 to-neon-pink",
  },
  {
    slug: "was-ist-ai-ugc",
    title: "Was ist AI UGC? Der komplette Guide 2026",
    excerpt: "Erfahre alles über KI-generierte Inhalte und wie sie das Marketing revolutionieren.",
    date: "5. Januar 2026",
    readTime: "8 Min. Lesezeit",
    category: "Guide",
    categoryColor: "from-neon-purple to-neon-pink",
  },
  {
    slug: "ugc-videos-erstellen-ohne-filmen",
    title: "UGC Videos erstellen ohne Filmen",
    excerpt: "So funktioniert AI UGC: Professionelle Videos ohne Kamera, ohne Aufwand.",
    date: "4. Januar 2026",
    readTime: "6 Min. Lesezeit",
    category: "Tutorial",
    categoryColor: "from-neon-cyan to-neon-purple",
  },
  {
    slug: "fuel72-erfahrungen",
    title: "Fuel72 Erfahrungen: Echte Kundenstimmen",
    excerpt: "Was sagen echte Kunden über Fuel72? Erfahrungsberichte und Bewertungen.",
    date: "3. Januar 2026",
    readTime: "5 Min. Lesezeit",
    category: "Erfahrungen",
    categoryColor: "from-orange-500 to-neon-pink",
  },
];

const floatingIcons = [
  { emoji: "📚", top: "15%", left: "8%", delay: "0s" },
  { emoji: "✨", top: "25%", right: "10%", delay: "1s" },
  { emoji: "🚀", top: "45%", left: "5%", delay: "2s" },
  { emoji: "💡", top: "60%", right: "8%", delay: "0.5s" },
];

const Blog = () => {
  const { t } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isLoaded) {
      const timer = setTimeout(() => setShowContent(true), 300);
      return () => clearTimeout(timer);
    }
  }, [isLoaded]);

  return (
    <div className="min-h-screen gradient-mesh overflow-hidden">
      <Helmet>
        <title>AI UGC Blog - Tips, Guides & Case Studies | Fuel72</title>
        <meta
          name="description"
          content="Learn about AI UGC video generation, content marketing strategies, and see real case studies. Expert insights from Fuel72."
        />
        <meta
          name="keywords"
          content="AI UGC blog, UGC marketing tips, AI video generator guide, content marketing, Fuel72 blog"
        />
        <link rel="canonical" href="https://www.fuel72.com/blog" />
        <meta property="og:title" content="AI UGC Blog - Tips, Guides & Case Studies | Fuel72" />
        <meta property="og:description" content="Learn about AI UGC video generation and content marketing. Expert insights from Fuel72." />
        <meta property="og:url" content="https://www.fuel72.com/blog" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Navbar />

      {/* Floating Icons */}
      <FloatingIcons desktopIcons={floatingIcons} />

      <main className={`pt-24 pb-16 transition-all duration-700 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <div 
            className="text-center mb-16 animate-section-entrance"
            style={{ animationDelay: "0.1s" }}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-neon-purple/20 to-neon-pink/20 border border-neon-purple/30 text-sm font-medium mb-6 animate-pulse-glow">
              📚 {t("blog.pageTitle")}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
              {t("blog.pageHeadline")}{" "}
              <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 bg-clip-text text-transparent animate-gradient-shift bg-[length:200%_auto]">
                {t("blog.pageHeadlineHighlight")}
              </span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("blog.pageSubtitle")}
            </p>
          </div>

          {/* English Articles */}
          <section 
            className="mb-16 animate-section-entrance"
            style={{ animationDelay: "0.2s" }}
          >
            <h2 className="text-2xl font-heading font-bold mb-8 flex items-center gap-3">
              <span className="text-3xl">🇺🇸</span> {t("blog.englishArticles")}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {blogPosts.map((post, index) => (
                <div 
                  key={post.slug}
                  className="animate-section-entrance"
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <BlogCard {...post} />
                </div>
              ))}
            </div>
          </section>

          {/* German Articles */}
          <section 
            className="animate-section-entrance"
            style={{ animationDelay: "0.5s" }}
          >
            <h2 className="text-2xl font-heading font-bold mb-8 flex items-center gap-3">
              <span className="text-3xl">🇩🇪</span> {t("blog.germanArticles")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {germanPosts.map((post, index) => (
                <div 
                  key={post.slug}
                  className="animate-section-entrance"
                  style={{ animationDelay: `${0.6 + index * 0.1}s` }}
                >
                  <BlogCard {...post} />
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
      
      {/* Sticky CTAs */}
      <StickyMobileCTA />
      <DesktopFloatingCTA />
    </div>
  );
};

export default Blog;